import { io, Socket } from 'socket.io-client';

// Derive the socket base URL from the API URL.
// API URL is like https://api.ibexgames.fun/api
// Socket URL must be the root: https://api.ibexgames.fun
function getSocketUrl(): string {
  // 1. Explicit socket URL env var takes priority
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  // 2. Derive from API URL by stripping the /api path
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
  }
  // 3. Hardcoded fallback — must match the actual backend host
  return 'https://api.ibexgames.fun';
}

class SocketService {
  private socket: Socket | null = null;

  connect(token?: string) {
    // Don't create a second connection if already connected
    if (this.socket?.connected) return this.socket;

    const socketUrl = getSocketUrl();

    this.socket = io(socketUrl, {
      auth: {
        token: token || localStorage.getItem('admin_token'),
      },
      // Try WebSocket first, fall back to polling if WS is blocked
      transports: ['websocket', 'polling'],
      // Reconnection settings
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      console.log('[Socket] Connected:', this.socket?.id, '→', socketUrl);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message, '→', socketUrl);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  getSocket() {
    return this.socket;
  }

  // Listen for deposit created event
  onDepositCreated(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('depositCreated', callback);
    }
  }

  // Listen for withdrawal created event
  onWithdrawalCreated(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('withdrawalCreated', callback);
    }
  }

  // ── Room real-time events ──────────────────────────────────────────────────

  /** Player joined a room: { userId, username, roomId, stake, currentPlayers, status } */
  onGlobalRoomJoined(callback: (data: any) => void) {
    this.socket?.on('globalRoomJoined', callback);
  }
  offGlobalRoomJoined(callback?: (data: any) => void) {
    this.socket?.off('globalRoomJoined', callback);
  }

  /** Player left a room: { userId, username, roomId, stake, currentPlayers, status } */
  onGlobalRoomLeft(callback: (data: any) => void) {
    this.socket?.on('globalRoomLeft', callback);
  }
  offGlobalRoomLeft(callback?: (data: any) => void) {
    this.socket?.off('globalRoomLeft', callback);
  }

  /** Room status changed: { roomId, stake, currentPlayers, status } */
  onGlobalRoomUpdated(callback: (data: any) => void) {
    this.socket?.on('globalRoomUpdated', callback);
  }
  offGlobalRoomUpdated(callback?: (data: any) => void) {
    this.socket?.off('globalRoomUpdated', callback);
  }

  // Remove listeners
  offDepositCreated(callback?: (data: any) => void) {
    if (this.socket) {
      this.socket.off('depositCreated', callback);
    }
  }

  offWithdrawalCreated(callback?: (data: any) => void) {
    if (this.socket) {
      this.socket.off('withdrawalCreated', callback);
    }
  }
}

export const socketService = new SocketService();
export default socketService;
